import React, { useEffect, useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import {
  Button,
  EmptyState,
  EmptyStateProps,
  OneLineList,
  Pagination,
  RowActionsMenu,
  SkeletonCard,
  TABLE_BODY_SKELETON,
  failedEmptyState,
  searchEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import ExerciseFormModal from "./ExerciseFormModal";
import SendExerciseModal from "./SendExerciseModal";
import ViewExerciseModal from "./ViewExerciseModal";
// A deep import rather than the clients barrel: that barrel pulls in the
// client pages, and the client module imports this panel back for its Plans
// tab.
import ChooseClientModal, {
  ChosenClient,
} from "../../clients/components/ChooseClientModal";
import { useExercisePlans } from "../hooks/useExercisePlans";
import { formatDuration, formatExerciseDate } from "../exerciseMapping";
import { saveExercisePlanPdf } from "../exerciseDocument";
import {
  DEFAULT_PAGE_SIZE,
  fetchExercisePlanPdf,
} from "../services/exercisePlansService";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { ApiError } from "../../../lib/http";
import type { SubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
import type {
  ExerciseEntry,
  ExercisePlanSummary,
} from "../types/exercisePlans.types";

const headCell =
  "px-3 sm:px-4 py-3 text-[13px] font-normal text-ink-muted bg-white shadow-[inset_0_-1px_0_#E9EDF1]";
const bodyCell = "px-3 sm:px-4 py-4 text-[14px] text-ink";

/**
 * Which dialog is up, and over what.
 *
 * One value rather than three flags, because they are steps and not states
 * that can hold at once, and two dialogs stacked would leave a reader unsure
 * which one their Escape answers. The same arrangement `ClientDetailPage`
 * makes of its review steps.
 *
 * `client` is the step before `create` on the workspace-wide list, which has
 * nobody to write for until it is asked; a client's own panel goes straight
 * to `create`, carrying that client. `confirm` is the step after `create` —
 * the lines just typed, and where they are going — and the one that writes
 * the plan; cancelled, it goes back to `create` carrying those lines, which
 * is what `entries` on that step is for. `send` is the same dialog on a
 * stored plan, where the row menu's Send lands. `view` and `send` carry a
 * row, which is all the table has — and all either needs, since a written
 * plan is read and sent rather than rewritten.
 */
type Dialog =
  | { kind: "none" }
  | { kind: "client" }
  | { kind: "create"; client: ChosenClient; entries?: ExerciseEntry[] }
  | { kind: "confirm"; client: ChosenClient; entries: ExerciseEntry[] }
  | { kind: "view"; plan: ExercisePlanSummary }
  | { kind: "send"; plan: ExercisePlanSummary };

export interface ExercisePlansPanelProps {
  /**
   * The client's `public_id` — whose plans are listed. Left off, the panel
   * lists the whole workspace's, which is what the Create plan screen shows:
   * every row then names its client, and creating one asks who it is for
   * first.
   */
  clientId?: string;
  clientName?: string;
  /**
   * Whether this workspace may still write, read once by the screen above —
   * see `ClientDetailPage`. Writing an exercise plan is one of the things a
   * subscription pays for, so with none running the ways into the dialog are
   * off. The list itself stays readable, and so does the view dialog.
   */
  gate: SubscriptionGate;
}

/**
 * Exercise plans — a client's, or everyone's: the table, and the dialogs that
 * read and write one.
 *
 * The rows come from `GET /api/v1/exercise-plans/`, a page at a time,
 * filtered by `client` where the panel was given one, and a create and a send
 * each refresh what was fetched rather than being patched into it. A plan is
 * written once — there is no way back into the form on one that exists.
 *
 * Writing one and sending it are one step: the form's Send exercise opens
 * the send dialog on the lines as typed, where the addresses are — the same
 * dialog the row menu's Send opens on a stored plan — and its Send writes the
 * plan and then sends it. Nothing is written until then, so cancelling the
 * dialog goes back to the form with the lines still in it rather than
 * leaving a plan on the list that nobody meant to keep. The write and the
 * send are still two calls, though: a plan that was written and then refused
 * a send is on the list, and the dialog stays up on it to try again from.
 *
 * What the panel cannot do is delete: there is no endpoint for it, and
 * `exercisePlanStore` is still here with nothing calling it.
 */
const ExercisePlansPanel: React.FC<ExercisePlansPanelProps> = ({
  clientId,
  clientName,
  gate,
}) => {
  const toast = useToast();
  const getToken = useTokenGetter();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<Dialog>({ kind: "none" });
  /** The plan whose document is being fetched, if one is. */
  const [downloading, setDownloading] = useState<string | null>(null);

  const createGate = gate.props("create an exercise plan");
  const sendGate = gate.props("send an exercise plan");

  /**
   * The way in from the Create button: the form for the client this panel is
   * about, or the question of who it is for when it is about everyone.
   */
  const startCreate = () =>
    setDialog(
      clientId
        ? { kind: "create", client: { id: clientId, name: clientName ?? "" } }
        : { kind: "client" },
    );

  const {
    plans: fetched,
    count,
    status: loadStatus,
    error,
    refused,
    create,
    send,
  } = useExercisePlans({
    client: clientId,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();

    const all: ExercisePlanSummary[] = fetched;
    if (!query) return all;

    // Filtered here rather than by the endpoint, which takes no `search`. So
    // it narrows the page in hand rather than the list — which for a client
    // with one page of plans is the same thing, and for one with several is
    // not. The day the parameter lands this moves into the query, the way the
    // meal list's already is.
    //
    // The exercise names are searched too — the list row carries them, so
    // "yoga" finds a plan without anyone opening it.
    return all.filter(
      (plan) =>
        plan.name.toLowerCase().includes(query) ||
        plan.types.some((type) => type.toLowerCase().includes(query)),
    );
  }, [fetched, search]);

  // The server's count for this client, not the rows on screen: this table
  // only ever holds one page of them.
  const totalPages = Math.max(1, Math.ceil(count / DEFAULT_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * DEFAULT_PAGE_SIZE;

  // A list that shrank past the page in hand lands on the last one, rather
  // than leaving the table asking for a page that is no longer there.
  useEffect(() => {
    if (loadStatus === "ready" && page > totalPages) setPage(totalPages);
  }, [loadStatus, page, totalPages]);

  // A page that arrives quickly goes straight from blank to filled: the
  // placeholders are only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(loadStatus === "loading");

  /**
   * A failure comes before emptiness: an empty table under "no exercise plans"
   * would read as an answer, when nothing was ever heard back.
   *
   * A refusal is the exception — 403 is what a workspace with no plan running
   * gets, and a practice that has never been able to write an exercise plan
   * has never written one. That is drawn as the empty list it is.
   */
  const emptyState = (): EmptyStateProps => {
    if (loadStatus === "failed" && !refused) {
      const state = failedEmptyState(
        "We could not load this client’s exercise plans",
      );
      return error ? { ...state, description: error } : state;
    }

    if (search.trim()) return searchEmptyState("exercise plans");

    return {
      title: "No exercise plans yet",
      description: clientName
        ? `Write ${clientName.split(" ")[0]} a day of exercise and it will be listed here with what is on it and how long it runs.`
        : "Write a client a day of exercise and it will be listed here with what is on it and how long it runs.",
      action: (
        <Button
          size="sm"
          startIcon={<Plus className="h-4 w-4" />}
          onClick={startCreate}
          {...createGate}
        >
          Create exercise
        </Button>
      ),
    };
  };

  /** What a refused request tells the user, in the words the API used. */
  const reportFailure = (what: string, reason: unknown) =>
    toast.error(
      what,
      reason instanceof ApiError
        ? reason.message
        : "We could not reach the server. Check your connection and try again.",
    );

  /**
   * Sends the plan to the addresses given, then closes the dialog on it.
   *
   * Left open on a refusal, the same way the form is — the addresses that
   * were typed are still there to try again with. See `SendExerciseModal`.
   */
  const handleSend = async (plan: ExercisePlanSummary, emails: string[]) => {
    try {
      const message = await send(plan.id, emails);
      setDialog({ kind: "none" });
      // The backend's own wording where it gave one — a change there reaches
      // the dietitian without this line being touched.
      toast.success(
        "Exercise sent",
        message ?? `${plan.shortName} is on its way to ${plan.clientName}.`,
      );
    } catch (reason) {
      reportFailure("Could not send the exercise", reason);
    }
  };

  /**
   * Fetches the document and hands it to the browser.
   *
   * The bytes rather than a link: the endpoint wants the same token every
   * other call does, so a plain `<a href>` to it would be an unauthenticated
   * request. See `saveExercisePlanPdf`, which is what happens to them.
   */
  const handleDownload = async (plan: ExercisePlanSummary) => {
    if (downloading) return;

    setDownloading(plan.id);
    try {
      saveExercisePlanPdf(plan, await fetchExercisePlanPdf(getToken, plan.id));
    } catch (reason) {
      reportFailure("Could not download the exercise plan", reason);
    } finally {
      setDownloading(null);
    }
  };

  /**
   * Writes the plan, then sends it — the send dialog's Send, on lines that
   * are not a plan yet.
   *
   * The dialog is left where it is if the write is refused, holding the
   * addresses; Cancel from there is still the way back to the form, which
   * has everything that was typed. The send is a second call on the plan the
   * first one answered with, which is why it cannot go until there is a
   * `public_id` to send to — and if that second call is the one refused, the
   * plan is written whatever the dialog does next. So the dialog moves onto
   * the stored plan, and its Send from then on is a send and not another
   * write. Nothing is said about the write itself: the one toast comes when
   * the send has answered.
   */
  const handleCreateAndSend = async (
    forClient: ChosenClient,
    entries: ExerciseEntry[],
    emails: string[],
  ) => {
    let plan: ExercisePlanSummary;
    try {
      plan = await create(entries, forClient.id);
    } catch (reason) {
      reportFailure("Could not save the exercise", reason);
      return;
    }

    // The backend names and numbers a new plan, and puts it at the top of
    // the list — so the table goes to the page it is actually on, with
    // nothing filtering it out.
    setPage(1);
    setSearch("");
    setDialog({ kind: "send", plan });

    await handleSend(plan, emails);
  };

  return (
    <section className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-md border border-line bg-white">
      <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 sm:px-6 py-5">
        <h2 className="text-[16px] 3xl:text-[18px] font-medium text-ink">
          Exercise
        </h2>
        <div className="flex items-center gap-3">
          <div className="relative w-full sm:w-[220px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search"
              aria-label="Search exercise plans"
              className="w-full rounded-md border border-line py-2 pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-subtle focus:outline-none focus:border-brand"
            />
          </div>
          <Button
            size="sm"
            startIcon={<Plus className="h-4 w-4" />}
            onClick={startCreate}
            {...createGate}
          >
            Create
          </Button>
        </div>
      </div>

      {/* Said as soon as the request goes out, whether or not anything is drawn
          for it: this line is not seen, so it costs nothing to be early, and
          the skeleton it stands for is hidden from screen readers — it is the
          shape of an answer rather than the answer. */}
      {loadStatus === "loading" && (
        <p role="status" className="sr-only">
          Loading exercise plans…
        </p>
      )}

      {phase !== "ready" && rows.length === 0 ? (
        // One block the size of the table, rather than a page of blocks in the
        // shape of its cells — see `TABLE_BODY_SKELETON`.
        <div className="flex-1 min-h-0 p-4 sm:p-6">
          <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-overlay">
          {/* Full height only when the rows are gone, so the empty state sits
              in the middle of the card rather than under a header. */}
          <table
            className={`w-full min-w-[760px] table-fixed border-collapse text-left ${
              rows.length === 0 ? "h-full" : ""
            }`}
          >
            {/* Sl at 5%, the same as the meal and supplement tables — the
                three Plans tabs are read one after the other, so a number
                column that changed width between them would read as the table
                shifting rather than as the columns differing.

                Plan name takes what Sl and Exercise type give up: the exercise
                names are one word each — "Yoga", "Walking" — and go behind a
                +n count once they stop fitting, where a plan's name truncates
                into nothing that tells the rows apart. */}
            {/* Listing everyone, a Client name column goes in after the
                plan's, and the plan name gives up the width for it — on that
                list the client is what tells the rows apart. */}
            {clientId ? (
              <colgroup>
                <col className="w-[5%]" />
                <col className="w-[30%]" />
                <col className="w-[24%]" />
                <col className="w-[18%]" />
                <col className="w-[18%]" />
                <col className="w-[5%]" />
              </colgroup>
            ) : (
              <colgroup>
                <col className="w-[5%]" />
                <col className="w-[24%]" />
                <col className="w-[18%]" />
                <col className="w-[20%]" />
                <col className="w-[14%]" />
                <col className="w-[14%]" />
                <col className="w-[5%]" />
              </colgroup>
            )}
            <thead className="sticky top-0 z-10">
              <tr>
                <th scope="col" className={headCell}>
                  Sl
                </th>
                <th scope="col" className={headCell}>
                  Plan name
                </th>
                {!clientId && (
                  <th scope="col" className={headCell}>
                    Client name
                  </th>
                )}
                <th scope="col" className={headCell}>
                  Exercise type
                </th>
                <th scope="col" className={headCell}>
                  Total duration
                </th>
                <th scope="col" className={headCell}>
                  Created on
                </th>
                <th scope="col" className={headCell}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={clientId ? 6 : 7} className="align-middle">
                    <EmptyState {...emptyState()} />
                  </td>
                </tr>
              ) : (
                rows.map((plan, index) => (
                  <tr
                    key={plan.id}
                    onClick={() => setDialog({ kind: "view", plan })}
                    className="cursor-pointer border-b border-line transition-colors last:border-0 hover:bg-[#F8FEFF]"
                  >
                    <td className={bodyCell}>
                      {String(startIndex + index + 1).padStart(2, "0")}
                    </td>
                    <td className={`${bodyCell} truncate font-medium`}>
                      {/* The whole row is clickable for pointers, but the name
                          is a real button so the list stays reachable by
                          keyboard — the same arrangement the meal table uses.
                          The whole of the backend's name, truncated where the
                          column runs out, with the `title` to read it by. */}
                      <button
                        type="button"
                        title={plan.name}
                        onClick={(event) => {
                          event.stopPropagation();
                          setDialog({ kind: "view", plan });
                        }}
                        className="max-w-full truncate rounded text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                      >
                        {plan.name}
                      </button>
                    </td>
                    {!clientId && (
                      <td className={`${bodyCell} truncate`}>
                        {plan.clientName}
                      </td>
                    )}
                    {/* `min-w-0` on the cell, because `OneLineList` measures
                        itself against whatever contains it — given its
                        content's width it would never overflow, and so would
                        never count anything behind the pill. */}
                    <td className={`${bodyCell} min-w-0`}>
                      {/* The names the list row carries — `exercise_names` —
                          rather than how many there are. `OneLineList` draws a
                          dash for a plan with none. */}
                      <OneLineList items={plan.types} label="exercises" />
                    </td>
                    <td className={bodyCell}>{formatDuration(plan.minutes)}</td>
                    <td className={bodyCell}>
                      {formatExerciseDate(plan.createdOn)}
                    </td>
                    {/* The menu stops its own clicks, so opening it does not
                        also open the plan the row belongs to. */}
                    <td
                      className={bodyCell}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <RowActionsMenu
                        label={`Actions for ${plan.shortName}`}
                        actions={[
                          {
                            label: "View exercise",
                            onSelect: () => setDialog({ kind: "view", plan }),
                          },
                          {
                            // What the row says depends on whether this plan
                            // has been to the client before, the same way the
                            // supplement menu reads.
                            label: plan.lastSentAt
                              ? "Send again"
                              : "Send exercise",
                            disabled: sendGate.disabled,
                            onSelect: sendGate.disabled
                              ? (sendGate.onDisabledClick ?? (() => {}))
                              : () => setDialog({ kind: "send", plan }),
                          },
                          {
                            label:
                              downloading === plan.id
                                ? "Preparing PDF…"
                                : "Download PDF",
                            // Reading is never gated, and a document is a
                            // read. Only one at a time, because the row has
                            // no way to say which of two it is preparing.
                            disabled: downloading !== null,
                            onSelect: () => void handleDownload(plan),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setPage}
        resultCount={rows.length}
      />

      {dialog.kind === "client" && (
        <ChooseClientModal
          title="Create exercise"
          label="Choose client for exercise"
          onClose={() => setDialog({ kind: "none" })}
          onChoose={(client) => setDialog({ kind: "create", client })}
        />
      )}

      {dialog.kind === "create" && (
        <ExerciseFormModal
          initialEntries={dialog.entries}
          onClose={() => setDialog({ kind: "none" })}
          onSubmit={(entries) =>
            setDialog({ kind: "confirm", client: dialog.client, entries })
          }
        />
      )}

      {dialog.kind === "view" && (
        <ViewExerciseModal
          planId={dialog.plan.id}
          exerciseCount={dialog.plan.exerciseCount}
          onClose={() => setDialog({ kind: "none" })}
        />
      )}

      {/* One element for both steps, so a dialog that moves from `confirm`
          to `send` mid-send — the write took, the send did not — keeps the
          addresses that were typed rather than being remounted empty. */}
      {(dialog.kind === "confirm" || dialog.kind === "send") && (
        <SendExerciseModal
          plan={
            dialog.kind === "send"
              ? dialog.plan
              : {
                  clientId: dialog.client.id,
                  clientName: dialog.client.name,
                  entries: dialog.entries,
                }
          }
          // Cancel on lines not written yet goes back to the form, not the
          // list: nothing has happened, and the lines are still there to
          // change. On a stored plan there is nothing to go back to.
          onClose={() =>
            setDialog(
              dialog.kind === "send"
                ? { kind: "none" }
                : {
                    kind: "create",
                    client: dialog.client,
                    entries: dialog.entries,
                  },
            )
          }
          onSend={(emails) =>
            dialog.kind === "send"
              ? handleSend(dialog.plan, emails)
              : handleCreateAndSend(dialog.client, dialog.entries, emails)
          }
        />
      )}
    </section>
  );
};

export default ExercisePlansPanel;
